import {
  dataDb,
  DynamicFormEntry,
  ProgramEntry,
  ContactEntry,
  PropertyEntry,
  ProgramMapFeaturesEntry,
  MyProgramsEntry,
  FileToUpload,
  FileToDelete,
} from './databases/data-db';
import { Injectable } from '@angular/core';
import { LoggingService } from '../../services/logging.service';
import { Program } from '../programs/models/program.model';
import { historyDb, HistoryEntry } from './databases/history-db';
import { v4 as uuidv4 } from 'uuid';
import { Property } from '../properties/models/property.model';
import { CurrentProposition } from '../maps/maps-proposition/map-proposition.interface';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  constructor(private readonly logger: LoggingService) {}

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public populateMyPrograms(programs: any): void {
    if (!programs) {
      this.logger.info('populateMyPrograms - no programs provided');
      return;
    }
    // delete all existing to replace by provided item
    dataDb.myPrograms
      .toCollection()
      .delete()
      .then((deleteCount) => {
        this.logger.info('populateMyPrograms deleted ' + deleteCount + ' program objects');
        const dateAdded = Date.now();
        const programEntries: Array<MyProgramsEntry> = [];
        programEntries.push({
          id: 'myprograms',
          timestamp: dateAdded,
          content: programs,
        });
        dataDb.myPrograms
          .bulkPut(programEntries)
          .then(() => {
            this.logger.info('populated my programs', programEntries.length);
          })
          .catch((error) => {
            this.logger.error(error, false);
          });
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  public populatePrograms(programs: Array<Program>): void {
    if (!programs || programs.length === 0) {
      this.logger.info('populatePrograms - no programs provided');
      return;
    }
    // delete all existing to replace by provided array
    dataDb.programs
      .toCollection()
      .delete()
      .then((deleteCount) => {
        this.logger.info('populatePrograms deleted ' + deleteCount + ' program objects');
        const dateAdded = Date.now();
        const programEntries: Array<ProgramEntry> = [];
        for (const program of programs) {
          programEntries.push({
            _id: program._id,
            timestamp: dateAdded,
            content: program,
            currency: '',
          });
        }
        dataDb.programs
          .bulkPut(programEntries)
          .then(() => {
            this.logger.info('populated programs', programEntries.length);
          })
          .catch((error) => {
            this.logger.error(error, false);
          });
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  public populateProperties(properties: Array<any>): void {
    if (!properties || properties.length === 0) {
      this.logger.info('populateProperties - no properties provided');
      return;
    }
    for (const property of properties) {
      const clone = JSON.parse(JSON.stringify(property));
      dataDb.properties
        .get({ _id: property._id })
        .then((propertyEntry) => {
          if (propertyEntry) {
            if (!propertyEntry.isModified) {
              dataDb.properties
                .update(propertyEntry._id, {
                  timestamp: property.created || Date.now(),
                  content: clone,
                })
                .then(() => {
                  this.logger.debug('updated property', clone);
                })
                .catch((error) => {
                  this.logger.error(error, false);
                });
            }
          } else {
            dataDb.properties
              .add({
                isNew: false,
                isModified: false,
                isArchived: 0,
                _id: clone._id,
                timestamp: property.created || Date.now(),
                content: clone,
                filesToUpload: [],
                filesToDelete: [],
              })
              .then(() => {
                this.logger.debug('added property', clone);
              })
              .catch((error) => {
                this.logger.error(error, false);
              });
          }
        })
        .catch((error) => {
          this.logger.error(error, false);
        });
    }
  }

  public populateSystemInstances(systemInstances: Array<any>): void {
    if (!systemInstances || systemInstances.length === 0) {
      this.logger.info('populateSystemInstances - no systemInstances provided');
      return;
    }
    for (const systemInstance of systemInstances) {
      const clone = JSON.parse(JSON.stringify(systemInstance));
      dataDb.systemInstances
        .get({ _id: systemInstance._id })
        .then((systemInstanceEntry) => {
          if (systemInstanceEntry) {
            if (!systemInstanceEntry.isModified) {
              dataDb.systemInstances
                .update(systemInstanceEntry._id, {
                  timestamp: systemInstance.created || Date.now(),
                  content: clone,
                })
                .then(() => {
                  this.logger.debug('updated systemInstance', clone);
                })
                .catch((error) => {
                  this.logger.error(error, false);
                });
            }
          } else {
            dataDb.systemInstances
              .add({
                isNew: false,
                isModified: false,
                isArchived: 0,
                _id: clone._id,
                timestamp: systemInstance.created || Date.now(),
                content: clone,
              })
              .then(() => {
                this.logger.debug('added systemInstance', clone);
              })
              .catch((error) => {
                this.logger.error(error, false);
              });
          }
        })
        .catch((error) => {
          this.logger.error(error, false);
        });
    }
  }

  private static getCorrespondingPropertyEntry(
    property: any,
    propertyEntries: PropertyEntry[],
  ): PropertyEntry | undefined {
    return propertyEntries.find((x) => x._id === property._id);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public populateProgramMapFeatures(params: string, programMapFeatures: any): void {
    if (!programMapFeatures) {
      this.logger.info('populateProgramMapFeatures - no programMapFeatures provided');
      return;
    }
    const dateAdded = Date.now();

    const clone = JSON.parse(JSON.stringify(programMapFeatures));
    dataDb.programMapFeatures
      .get({ _id: params })
      .then((programMapFeatureEntry) => {
        if (programMapFeatureEntry) {
          dataDb.programMapFeatures
            .update(params, {
              timestamp: dateAdded,
              content: clone,
            })
            .then(() => {
              this.logger.debug('updated programMapFeature', clone);
            })
            .catch((error) => {
              this.logger.error(error, false);
            });
        } else {
          dataDb.programMapFeatures
            .add({
              _id: params,
              timestamp: dateAdded,
              content: clone,
            })
            .then(() => {
              this.logger.debug('added programMapFeature', clone);
            })
            .catch((error) => {
              this.logger.error(error, false);
            });
        }
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  private static getCorrespondingProgramMapFeaturesEntry(
    programMapId: string,
    programMapFeatureEntries: ProgramMapFeaturesEntry[],
  ): ProgramMapFeaturesEntry | undefined {
    return programMapFeatureEntries.find((x) => x._id === programMapId);
  }

  public getProgramMapFeatures(params: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      dataDb.programMapFeatures
        .get({ _id: params })
        .then((programMapFeatures) => {
          if (programMapFeatures) {
            resolve(programMapFeatures.content);
          } else {
            resolve({
              type: 'FeatureCollection',
              features: [],
            });
          }
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public populateDynamicForms(dynamicForms: Array<any>, collection: string): void {
    if (!dynamicForms || dynamicForms.length === 0) {
      this.logger.info('populateDynamicForms - no dynamicForms provided');
      return;
    }
    // if there are multiple times the same dynamicForm in the list, it will fail on
    // subsequent inserts which is ok
    for (const dynamicForm of dynamicForms) {
      dataDb.dynamicForms
        .get({ _id: dynamicForm._id })
        .then((dynamicFormEntry) => {
          if (dynamicFormEntry) {
            if (!dynamicFormEntry.isModified) {
              dataDb.dynamicForms
                .update(dynamicFormEntry._id, {
                  isNew: false,
                  isModified: false,
                  lastModified: dynamicForm.lastModified,
                  definitionId: dynamicForm.definitionId,
                  content: dynamicForm,
                  filesToUpload: [],
                  filesToDelete: [],
                })
                .then(() => {
                  this.logger.debug('updated dynamicForm', dynamicForm);
                })
                .catch((error) => {
                  this.logger.error(error, false);
                });
            }
          } else {
            dataDb.dynamicForms
              .add({
                _id: dynamicForm._id,
                timestamp: dynamicForm.created || Date.now(),
                isNew: false,
                isModified: false,
                isArchived: 0,
                lastModified: dynamicForm.lastModified,
                definitionId: dynamicForm.definitionId,
                content: dynamicForm,
                collection: collection,
                filesToUpload: [],
                filesToDelete: [],
              })
              .then(() => {
                this.logger.debug('added dynamicForm', dynamicForm);
              })
              .catch((error) => {
                this.logger.error(error, false);
              });
          }
        })
        .catch((error) => {
          this.logger.error(error, false);
        });
    }
  }

  private static getCorrespondingDynamicFormEntry(
    dynamicForm: any,
    dynamicFormEntries: DynamicFormEntry[],
  ): DynamicFormEntry | undefined {
    return dynamicFormEntries.find((x) => x._id === dynamicForm._id);
  }

  public getDynamicForms(definitionId: string, skip: number, limit: number): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const dynamicForms: any[] = [];
      dataDb.dynamicForms
        .where({ isArchived: 0, definitionId })
        .reverse()
        .offset(skip)
        .limit(limit)
        .sortBy('timestamp')
        .then((entries) => {
          for (const entry of entries) {
            dynamicForms.push(entry.content);
          }
          resolve(dynamicForms);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getDynamicFormsCount(definitionId: string): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      dataDb.dynamicForms
        .where({ isArchived: 0, definitionId })
        .reverse()
        .count()
        .then((count) => {
          resolve(count);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getAllDynamicFormEntriesWithoutContent(): Promise<any[]> {
    const start = Date.now();
    return new Promise<any[]>((resolve, reject) => {
      const dynamicForms: any[] = [];
      dataDb.dynamicForms
        .where({ isArchived: 0 })
        .toArray()
        .then((entries) => {
          for (const entry of entries) {
            delete entry.content;
            dynamicForms.push(entry);
          }
          const elapsed = Date.now() - start;
          if (elapsed > 10000) {
            this.logger.error(
              'indexDB getAllDynamicFormEntriesWithoutContent - very long indexDB operation ' + elapsed,
              false,
            );
          }
          resolve(dynamicForms);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getAllContactEntriesWithoutContent(): Promise<any[]> {
    const start = Date.now();
    return new Promise<any[]>((resolve, reject) => {
      const contacts: any[] = [];
      dataDb.contacts
        .where({ isArchived: 0 })
        .toArray()
        .then((entries) => {
          for (const entry of entries) {
            delete entry.content;
            contacts.push(entry);
          }
          const elapsed = Date.now() - start;
          if (elapsed > 10000) {
            this.logger.error(
              'indexDB getAllContactEntriesWithoutContent - very long indexDB operation ' + elapsed,
              false,
            );
          }
          resolve(contacts);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getAllPropertyEntriesWithoutContent(): Promise<any[]> {
    const start = Date.now();
    return new Promise<any[]>((resolve, reject) => {
      const properties: any[] = [];
      dataDb.properties
        .where({ isArchived: 0 })
        .toArray()
        .then((entries) => {
          for (const entry of entries) {
            delete entry.content;
            properties.push(entry);
          }
          const elapsed = Date.now() - start;
          if (elapsed > 10000) {
            this.logger.error(
              'indexDB getAllPropertyEntriesWithoutContent - very long indexDB operation ' + elapsed,
              false,
            );
          }
          resolve(properties);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public populateContacts(contacts: Array<any>): void {
    if (!contacts || contacts.length === 0) {
      this.logger.info('populateContacts - no contacts provided');
      return;
    }
    for (const contact of contacts) {
      dataDb.contacts
        .get({ _id: contact._id })
        .then((contactEntry) => {
          if (contactEntry) {
            if (!contactEntry.isModified) {
              dataDb.contacts
                .update(contactEntry._id, {
                  isNew: false,
                  isModified: false,
                  lastModified: contact.lastModified,
                  content: contact,
                  filesToUpload: [],
                  filesToDelete: [],
                })
                .then(() => {
                  this.logger.debug('updated contact', contact);
                })
                .catch((error) => {
                  this.logger.error(error, false);
                });
            }
          } else {
            dataDb.contacts
              .add({
                _id: contact._id,
                timestamp: Date.now(),
                isNew: false,
                isModified: false,
                isArchived: 0,
                lastModified: contact.lastModified,
                content: contact,
                filesToUpload: [],
                filesToDelete: [],
              })
              .then(() => {
                this.logger.debug('added contact', contact);
              })
              .catch((error) => {
                this.logger.error(error, false);
              });
          }
        })
        .catch((error) => {
          this.logger.error(error, false);
        });
    }
  }

  private static getCorrespondingContactEntry(contact: any, contactEntries: ContactEntry[]): ContactEntry | undefined {
    return contactEntries.find((x) => x._id === contact._id);
  }

  public getContacts(skip: number, limit: number): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const contacts: any[] = [];
      dataDb.contacts
        .where({ isArchived: 0 })
        .reverse()
        .offset(skip)
        .limit(limit)
        .sortBy('timestamp')
        .then((entries) => {
          for (const entry of entries) {
            contacts.push(entry.content);
          }
          resolve(contacts);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getContactsCount(): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      dataDb.contacts
        .where({ isArchived: 0 })
        .reverse()
        .count()
        .then((count) => {
          resolve(count);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getPrograms(): Promise<Program[]> {
    return new Promise<Program[]>((resolve, reject) => {
      const programs: Program[] = [];
      dataDb.programs
        .toCollection()
        .reverse()
        .sortBy('timestamp')
        .then((entries) => {
          for (const entry of entries) {
            programs.push(entry.content);
          }
          resolve(programs);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getMyPrograms(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      dataDb.myPrograms
        .get('myprograms')
        .then((entry) => {
          if (entry) {
            resolve(entry.content);
          } else {
            resolve(null);
          }
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getProgram(id: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      dataDb.programs
        .get({ _id: id })
        .then((program) => {
          if (program) {
            resolve(program.content);
          } else {
            resolve(null);
          }
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public saveProgram(program: any): Promise<Program> {
    return new Promise<Program>((resolve, reject) => {
      dataDb.programs
        .get({ _id: program._id })
        .then((programEntry) => {
          if (programEntry) {
            const changes: any = {
              currency: program.currency,
              content: program,
            };
            dataDb.programs
              .update(program._id, changes)
              .then(() => {
                resolve(program);
              })
              .catch((error) => {
                this.logger.error(error, false);
                reject(error);
              });
          } else {
            reject(new Error('ProgramEntry not found: ' + program._id));
          }
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getProperties(skip: number, limit: number): Promise<Property[]> {
    return new Promise<Property[]>((resolve, reject) => {
      const properties: Property[] = [];
      dataDb.properties
        .where({ isArchived: 0 })
        .reverse()
        .offset(skip)
        .limit(limit)
        .sortBy('timestamp')
        .then((entries) => {
          for (const entry of entries) {
            properties.push(entry.content);
          }
          resolve(properties);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getPropertiesCount(): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      dataDb.properties
        .where({ isArchived: 0 })
        .reverse()
        .count()
        .then((count) => {
          resolve(count);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getDynamicFormsByFieldValue(
    definitionId: string,
    searchField: string,
    value: unknown,
    skip: number,
    limit: number,
  ): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const dynamicForms: any[] = [];
      dataDb.dynamicForms
        .where({ definitionId, isArchived: 0 })
        .reverse()
        .sortBy('timestamp')
        .then((entries) => {
          let index = 0;
          for (const entry of entries) {
            if (dynamicForms.length >= limit) {
              break;
            }
            if (!searchField) {
              if (index >= skip) {
                dynamicForms.push(entry.content);
              }
              index++;
            } else {
              let fieldValue = entry.content[searchField];
              if (searchField.indexOf('.') !== -1 && !Array.isArray(entry.content[searchField.split('.')[0]])) {
                if (entry.content[searchField.split('.')[0]]) {
                  fieldValue = entry.content[searchField.split('.')[0]][searchField.split('.')[1]];
                } else {
                  fieldValue = undefined;
                }
              }
              if (fieldValue === value) {
                if (index >= skip) {
                  dynamicForms.push(entry.content);
                }
                index++;
              }
            }
          }
          resolve(dynamicForms);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public searchDynamicFormsByValue(definitionId: string, programId: string, searchValue: string): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const dynamicForms: any[] = [];
      const withoutAccents = this.removeAccents(searchValue);
      dataDb.dynamicForms
        .where({ definitionId, isArchived: 0 })
        .reverse()
        .sortBy('timestamp')
        .then((entries) => {
          for (const entry of entries) {
            if (!programId || entry.content.programId === programId) {
              const content = this.removeAccents(JSON.stringify(entry.content));
              if (content.toLowerCase().indexOf(withoutAccents.toLowerCase()) !== -1) {
                dynamicForms.push(entry.content);
              }
            }
          }
          resolve(dynamicForms);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public searchContactsByValue(searchValue: string): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const contacts: any[] = [];
      const withoutAccents = this.removeAccents(searchValue);
      dataDb.contacts
        .where({ isArchived: 0 })
        .reverse()
        .sortBy('timestamp')
        .then((entries) => {
          for (const entry of entries) {
            if (
              this.removeAccents(entry.content.firstName).toLowerCase().indexOf(withoutAccents.toLowerCase()) !== -1 ||
              this.removeAccents(entry.content.lastName).toLowerCase().indexOf(withoutAccents.toLowerCase()) !== -1
            ) {
              contacts.push(entry.content);
            }
          }
          resolve(contacts);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getDynamicFormsByFieldValueCount(definitionId: string, searchField: string, value: unknown): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      const dynamicForms: any[] = [];
      dataDb.dynamicForms
        .where({ definitionId, isArchived: 0 })
        .reverse()
        .sortBy('timestamp')
        .then((entries) => {
          for (const entry of entries) {
            let fieldValue = entry.content[searchField];
            if (searchField.indexOf('.') !== -1 && !Array.isArray(entry.content[searchField.split('.')[0]])) {
              if (entry.content[searchField.split('.')[0]]) {
                fieldValue = entry.content[searchField.split('.')[0]][searchField.split('.')[1]];
              } else {
                fieldValue = undefined;
              }
            }
            if (fieldValue === value) {
              dynamicForms.push(entry.content);
            }
          }
          resolve(dynamicForms.length);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getPropertiesByFieldValue(
    searchField: string,
    value: unknown,
    skip: number,
    limit: number,
  ): Promise<Array<Property>> {
    return new Promise<Array<Property>>((resolve, reject) => {
      const properties: any[] = [];
      dataDb.properties
        .where({ isArchived: 0 })
        .reverse()
        .sortBy('timestamp')
        .then((entries) => {
          let index = 0;
          for (const entry of entries) {
            if (properties.length >= limit) {
              break;
            }
            let fieldValue = entry.content[searchField];
            if (searchField.indexOf('.') !== -1 && !Array.isArray(entry.content[searchField.split('.')[0]])) {
              if (entry.content[searchField.split('.')[0]]) {
                fieldValue = entry.content[searchField.split('.')[0]][searchField.split('.')[1]];
              } else {
                fieldValue = undefined;
              }
            }
            if (fieldValue === value) {
              if (index >= skip) {
                properties.push(entry.content);
              }
              index++;
            }
          }
          resolve(properties);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getSystemInstancesByFieldValue(
    searchField: string,
    value: unknown,
    skip: number,
    limit: number,
  ): Promise<Array<any>> {
    return new Promise<Array<any>>((resolve, reject) => {
      const systemInstances: any[] = [];
      dataDb.systemInstances
        .where({ isArchived: 0 })
        .reverse()
        .sortBy('timestamp')
        .then((entries) => {
          let index = 0;
          for (const entry of entries) {
            if (systemInstances.length >= limit) {
              break;
            }
            let fieldValue = entry.content[searchField];
            if (searchField.indexOf('.') !== -1 && !Array.isArray(entry.content[searchField.split('.')[0]])) {
              if (entry.content[searchField.split('.')[0]]) {
                fieldValue = entry.content[searchField.split('.')[0]][searchField.split('.')[1]];
              } else {
                fieldValue = undefined;
              }
            }
            if (fieldValue === value) {
              if (index >= skip) {
                systemInstances.push(entry.content);
              }
              index++;
            }
          }
          resolve(systemInstances);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getPropertiesByLandOwnerInfosRefs(ids: Array<string> | null = []): Promise<Array<Property>> {
    return new Promise<Array<Property>>((resolve, reject) => {
      const properties: any[] = [];
      if (!ids || ids.length === 0) {
        resolve(properties);
      }
      dataDb.properties
        .where({ isArchived: 0 })
        .reverse()
        .sortBy('timestamp')
        .then((entries) => {
          for (const entry of entries) {
            if (ids?.indexOf(entry.content.landOwnerInfosRef?.formInstanceId) !== -1) {
              properties.push(entry.content);
            }
          }
          resolve(properties);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getPropertiesByFieldValueCount(searchField: string, value: unknown): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      const properties: any[] = [];
      dataDb.properties
        .where({ isArchived: 0 })
        .reverse()
        .sortBy('timestamp')
        .then((entries) => {
          for (const entry of entries) {
            let fieldValue = entry.content[searchField];
            if (searchField.indexOf('.') !== -1 && !Array.isArray(entry.content[searchField.split('.')[0]])) {
              if (entry.content[searchField.split('.')[0]]) {
                fieldValue = entry.content[searchField.split('.')[0]][searchField.split('.')[1]];
              } else {
                fieldValue = undefined;
              }
            }
            if (fieldValue === value) {
              properties.push(entry.content);
            }
          }
          resolve(properties.length);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getDynamicForm(dynamicFormId: string): Promise<any> {
    const start = Date.now();
    return new Promise<any>((resolve, reject) => {
      dataDb.dynamicForms
        .get({ _id: dynamicFormId })
        .then((dynamicForm) => {
          if (dynamicForm && dynamicForm.isArchived === 0) {
            const elapsed = Date.now() - start;
            if (elapsed > 5000) {
              this.logger.error('indexDB getDynamicForm - very long indexDB operation ' + elapsed, false);
            }
            resolve(dynamicForm.content);
          } else {
            resolve(null);
          }
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getDynamicFormByIds(dynamicFormIds: Array<string>): Promise<Array<any>> {
    const start = Date.now();
    return new Promise<any>((resolve, reject) => {
      dataDb.dynamicForms
        .where('_id')
        .anyOf(dynamicFormIds)
        .toArray()
        .then((dynamicForms) => {
          const result = [];
          for (const dynamicForm of dynamicForms) {
            result.push(dynamicForm.content);
          }
          const elapsed = Date.now() - start;
          this.logger.warn('indexDB getDynamicFormByIds', dynamicForms.length + ' items', elapsed + 'ms');
          if (elapsed > 10000) {
            this.logger.error('indexDB getDynamicFormByIds - very long indexDB operation ' + elapsed, false);
          }
          resolve(result);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public saveDynamicForm(dynamicForm: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const dateModified = Date.now();
      dataDb.dynamicForms
        .get({ _id: dynamicForm.body.content._id })
        .then((dynamicFormEntry) => {
          if (dynamicFormEntry) {
            if (
              dynamicForm.body.filesToUpload?.length ||
              dynamicForm.body.filesToDelete?.length ||
              JSON.stringify(dynamicFormEntry.content) !== JSON.stringify(dynamicForm.body.content)
            ) {
              dynamicFormEntry.filesToUpload.push(...dynamicForm.body.filesToUpload);
              dynamicFormEntry.filesToDelete.push(...dynamicForm.body.filesToDelete);
              const changes: any = {
                isModified: true,
                lastModified: dateModified,
                timestamp: dynamicFormEntry.timestamp || Date.now(),
                content: dynamicForm.body.content,
                collection: dynamicForm.body.collection,
                filesToUpload: dynamicFormEntry.filesToUpload,
                filesToDelete: dynamicFormEntry.filesToDelete,
                changes: new deepDiffMapper().map(dynamicFormEntry.content, dynamicForm.body.content),
              };
              dataDb.dynamicForms
                .update(dynamicForm.body.content._id, changes)
                .then(() => {
                  resolve(dynamicForm.body.content);
                })
                .catch((error) => {
                  this.logger.error(error, false);
                  reject(error);
                });
            } else {
              resolve(dynamicForm.body.content);
            }
          } else {
            reject(new Error('DynamicFormEntry not found: ' + dynamicForm.body.content._id));
          }
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public deleteLocalDynamicForm(id: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      dataDb.dynamicForms
        .delete(id)
        .then((deleted) => {
          resolve(deleted);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public deleteLocalContact(id: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      dataDb.contacts
        .delete(id)
        .then((deleted) => {
          resolve(deleted);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public deleteLocalProperty(id: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      dataDb.properties
        .delete(id)
        .then((deleted) => {
          resolve(deleted);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public deleteLocalSystemInstance(id: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      dataDb.systemInstances
        .delete(id)
        .then((deleted) => {
          resolve(deleted);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public deleteLocalMapProposition(id: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      dataDb.mapPropositions
        .delete(id)
        .then((deleted) => {
          resolve(deleted);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public archiveDynamicForm(collection: string, formId: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      if (!collection || !formId) {
        this.logger.warn('collection and formId mandatory');
        reject('collection and formId mandatory');
      }
      const dateModified = Date.now();
      dataDb.dynamicForms
        .update(formId, {
          isArchived: 1,
          lastModified: dateModified,
          collection: collection,
        })
        .then(() => {
          resolve('item archived');
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public async createDynamicFormHistory(entry: { content: any }, resultCode = 200): Promise<void> {
    const history: HistoryEntry = {
      _id: uuidv4(),
      content: entry.content,
      timestamp: Date.now(),
      hasBeenTransferredSuccessfully: resultCode === 200,
      resultCode: resultCode,
    };

    try {
      await historyDb.history.add(history);
    } catch (error) {
      this.logger.error(error, false);
    }
  }

  public async createContactHistory(entry: { content: any }, resultCode = 200): Promise<void> {
    const history: HistoryEntry = {
      _id: uuidv4(),
      content: entry.content,
      timestamp: Date.now(),
      hasBeenTransferredSuccessfully: resultCode === 200,
      resultCode: resultCode,
    };

    try {
      await historyDb.history.add(history);
    } catch (error) {
      this.logger.error(error, false);
    }
  }

  public async createMapPropositionHistory(propositon: CurrentProposition, returnCode = 200): Promise<void> {
    const history: HistoryEntry = {
      _id: uuidv4(),
      content: propositon,
      timestamp: Date.now(),
      hasBeenTransferredSuccessfully: returnCode === 200,
      resultCode: returnCode,
    };

    try {
      await historyDb.history.add(history);
    } catch (error) {
      this.logger.error(error, false);
    }
  }

  public async createPropertyHistory(entry: { content: any }, resultCode = 200): Promise<void> {
    const history: HistoryEntry = {
      _id: uuidv4(),
      content: entry.content,
      timestamp: Date.now(),
      hasBeenTransferredSuccessfully: resultCode === 200,
      resultCode: resultCode,
    };

    try {
      await historyDb.history.add(history);
    } catch (error) {
      this.logger.error(error, false);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public createDynamicForm(dynamicForm: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const dateCreated = Date.now();
      dynamicForm.body.content._id = 'new_' + dateCreated;
      dynamicForm.body.content.created = dateCreated;
      dynamicForm.body.content.lastModified = dateCreated;
      const dynamicFormEntry: DynamicFormEntry = {
        _id: dynamicForm.body.content._id,
        isNew: true,
        timestamp: dateCreated,
        definitionId: dynamicForm.body.content.definitionId,
        isModified: false,
        isArchived: 0,
        lastModified: dateCreated,
        content: dynamicForm.body.content,
        collection: dynamicForm.body.collection,
        filesToUpload: dynamicForm.body.filesToUpload,
        filesToDelete: dynamicForm.body.filesToDelete,
      };
      dataDb.dynamicForms
        .add(dynamicFormEntry)
        .then(() => {
          resolve(dynamicForm.body.content);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getNewDynamicForms(): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const dynamicForms: any[] = [];
      dataDb.dynamicForms
        .where('timestamp')
        .above(0)
        .and((form) => {
          return form && form.isNew && !form.isArchived;
        })
        .limit(500)
        .toArray()
        .then((entries) => {
          for (const entry of entries) {
            dynamicForms.push({
              _id: entry._id,
              definitionId: entry.content.definitionId,
              content: entry.content,
              collection: entry.collection,
              filesToUpload: entry.filesToUpload,
              filesToDelete: entry.filesToDelete,
            });
          }
          resolve(dynamicForms);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getArchivedDynamicForms(): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const dynamicForms: any[] = [];
      dataDb.dynamicForms
        .where('timestamp')
        .above(0)
        .and((form) => {
          return form && form.isArchived === 1;
        })
        .limit(500)
        .toArray()
        .then((entries) => {
          for (const entry of entries) {
            dynamicForms.push({
              _id: entry._id,
              isNew: entry.isNew,
              isModified: entry.isModified,
              definitionId: entry.content.definitionId,
              content: entry.content,
              filesToUpload: entry.filesToUpload,
              filesToDelete: entry.filesToDelete,
              collection: entry.collection,
            });
          }
          resolve(dynamicForms);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getModifiedDynamicForms(): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const dynamicForms: any[] = [];
      dataDb.dynamicForms
        .where('timestamp')
        .above(0)
        .and((form) => {
          return form && !form.isNew && form.isModified && !form.isArchived;
        })
        .limit(500)
        .toArray()
        .then((entries) => {
          for (const entry of entries) {
            dynamicForms.push({
              _id: entry._id,
              definitionId: entry.content.definitionId,
              content: entry.content,
              collection: entry.collection,
              filesToUpload: entry.filesToUpload,
              filesToDelete: entry.filesToDelete,
            });
          }
          resolve(dynamicForms);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getContactsByFieldValue(searchField: string, value: unknown, skip: number, limit: number): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const contacts: any[] = [];
      dataDb.contacts
        .where({ isArchived: 0 })
        .reverse()
        .sortBy('timestamp')
        .then((entries) => {
          let index = 0;
          for (const entry of entries) {
            if (contacts.length >= limit) {
              break;
            }
            if (!searchField) {
              if (index >= skip) {
                contacts.push(entry.content);
              }
              index++;
            } else {
              let fieldValue = entry.content[searchField];
              if (searchField.indexOf('.') !== -1 && !Array.isArray(entry.content[searchField.split('.')[0]])) {
                if (entry.content[searchField.split('.')[0]]) {
                  fieldValue = entry.content[searchField.split('.')[0]][searchField.split('.')[1]];
                } else {
                  fieldValue = undefined;
                }
              }
              if (fieldValue === value) {
                if (index >= skip) {
                  contacts.push(entry.content);
                }
                index++;
              }
            }
          }
          resolve(contacts);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getContactsByFieldValueCount(searchField: string, value: unknown): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      const contacts: any[] = [];
      dataDb.contacts
        .where({ isArchived: 0 })
        .reverse()
        .sortBy('timestamp')
        .then((entries) => {
          for (const entry of entries) {
            let fieldValue = entry.content[searchField];
            if (searchField.indexOf('.') !== -1 && !Array.isArray(entry.content[searchField.split('.')[0]])) {
              if (entry.content[searchField.split('.')[0]]) {
                fieldValue = entry.content[searchField.split('.')[0]][searchField.split('.')[1]];
              } else {
                fieldValue = undefined;
              }
            }
            if (fieldValue === value) {
              contacts.push(entry.content);
            }
          }
          resolve(contacts.length);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getContact(contactId: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      dataDb.contacts
        .get({ _id: contactId })
        .then((contact) => {
          if (contact && contact.isArchived === 0) {
            resolve(contact?.content);
          } else {
            resolve(null);
          }
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public saveTileProposition(tile: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      dataDb.tilePropositions
        .add(tile)
        .then(() => {
          resolve(tile);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public saveProperty(property: any, filesToUpload: FileToUpload[], filesToDelete: FileToDelete[]): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const dateModified = Date.now();
      dataDb.properties
        .get({ _id: property._id })
        .then((propertyEntry) => {
          if (propertyEntry) {
            if (JSON.stringify(property) !== JSON.stringify(propertyEntry.content)) {
              const changes: any = {
                isModified: true,
                lastModified: dateModified,
                timestamp: propertyEntry.timestamp || Date.now(),
                content: property,
                filesToUpload: filesToUpload,
                filesToDelete: filesToDelete,
                changes: new deepDiffMapper().map(propertyEntry.content, property),
              };
              dataDb.properties
                .update(property._id, changes)
                .then(() => {
                  resolve(property);
                })
                .catch((error) => {
                  this.logger.error(error, false);
                  reject(error);
                });
            } else {
              resolve(property);
            }
          } else {
            reject(new Error('PropertyEntry not found: ' + property._id));
          }
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public createProperty(property: any, filesToUpload: FileToUpload[], filesToDelete: FileToDelete[]): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const dateCreated = Date.now();
      property._id = 'new_' + dateCreated;
      property.created = dateCreated;
      property.lastModified = dateCreated;
      const propertyEntry: PropertyEntry = {
        _id: property._id,
        isNew: true,
        timestamp: dateCreated,
        isModified: false,
        isArchived: 0,
        content: property,
        filesToUpload: filesToUpload,
        filesToDelete: filesToDelete,
      };
      dataDb.properties
        .add(propertyEntry)
        .then(() => {
          resolve(property);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getProperty(propertyId: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      dataDb.properties
        .get({ _id: propertyId })
        .then((property) => {
          if (property && property.isArchived === 0) {
            resolve(property?.content);
          } else {
            resolve(null);
          }
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public archiveProperty(propertyId: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      if (!propertyId) {
        reject('propertyId mandatory');
      }
      const dateModified = Date.now();
      dataDb.properties
        .update(propertyId, {
          isArchived: 1,
          lastModified: dateModified,
        })
        .then(() => {
          resolve('item archived');
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }
  public getMapPropositions(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      dataDb.mapPropositions
        .toArray()
        .then((mapPropositions) => {
          resolve(mapPropositions);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getMapProposition(propositionId: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      dataDb.mapPropositions
        .get({ id: propositionId })
        .then((mapProposition) => {
          resolve(mapProposition);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public saveMapProposition(mapProposition: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      dataDb.mapPropositions
        .put(mapProposition)
        .then(() => {
          resolve(mapProposition);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public async deleteMapProposition(propositionId: string): Promise<void> {
    return dataDb.mapPropositions.delete(propositionId);
  }

  public getTileProposition(tileId: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      dataDb.tilePropositions
        .get({ _id: tileId })
        .then((tile) => {
          resolve(tile?.content);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public saveContact(contact: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const dateModified = Date.now();
      dataDb.contacts
        .get({ _id: contact.body.content._id })
        .then((contactEntry) => {
          if (contactEntry) {
            if (
              contact.body.filesToUpload?.length ||
              contact.body.filesToDelete?.length ||
              JSON.stringify(contact.body.content) !== JSON.stringify(contactEntry.content)
            ) {
              contactEntry.filesToUpload.push(...contact.body.filesToUpload);
              contactEntry.filesToDelete.push(...contact.body.filesToDelete);
              const changes: any = {
                isModified: true,
                timestamp: contactEntry.timestamp || Date.now(),
                lastModified: dateModified,
                content: contact.body.content,
                filesToUpload: contactEntry.filesToUpload,
                filesToDelete: contactEntry.filesToDelete,
                changes: new deepDiffMapper().map(contactEntry.content, contact.body.content),
              };
              dataDb.contacts
                .update(contact.body.content._id, changes)
                .then(() => {
                  resolve(contact.body.content);
                })
                .catch((error) => {
                  this.logger.error(error, false);
                  reject(error);
                });
            } else {
              resolve(contact.body.content);
            }
          } else {
            reject(new Error('ContactEntry not found: ' + contact.body.content._id));
          }
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public archiveContact(contactId: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const dateModified = Date.now();
      dataDb.contacts
        .update(contactId, {
          isArchived: 1,
          lastModified: dateModified,
        })
        .then(() => {
          resolve('contact archived');
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public disableContact(contactId: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const dateModified = Date.now();
      dataDb.contacts
        .update(contactId, {
          disable: 1,
          lastModified: dateModified,
        })
        .then(() => {
          resolve('contact disabled');
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public createContact(contact: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const dateCreated = Date.now();
      contact.body.content._id = 'new_' + dateCreated;
      contact.body.content.created = dateCreated;
      contact.body.content.lastModified = dateCreated;
      const contactEntry: ContactEntry = {
        _id: contact.body.content._id,
        isNew: true,
        timestamp: dateCreated,
        isModified: false,
        isArchived: 0,
        lastModified: dateCreated,
        content: contact.body.content,
        filesToUpload: contact.body.filesToUpload,
        filesToDelete: contact.body.filesToDelete,
      };
      dataDb.contacts
        .add(contactEntry)
        .then(() => {
          resolve(contact.body.content);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getNewContacts(): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const contacts: any[] = [];
      dataDb.contacts
        .where('timestamp')
        .above(0)
        .and((contact) => {
          return contact && contact.isNew && !contact.isArchived;
        })
        .limit(500)
        .toArray()
        .then((entries) => {
          for (const entry of entries) {
            contacts.push({
              _id: entry._id,
              content: entry.content,
              filesToUpload: entry.filesToUpload,
              filesToDelete: entry.filesToDelete,
            });
          }
          resolve(contacts);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getArchivedContacts(): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const contacts: any[] = [];
      dataDb.contacts
        .where('timestamp')
        .above(0)
        .and((contact) => {
          return contact && contact.isArchived === 1;
        })
        .limit(500)
        .toArray()
        .then((entries) => {
          for (const entry of entries) {
            contacts.push({
              _id: entry._id,
              isNew: entry.isNew,
              isModified: entry.isModified,
              content: entry.content,
              filesToUpload: entry.filesToUpload,
              filesToDelete: entry.filesToDelete,
            });
          }
          resolve(contacts);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getModifiedContacts(): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const contacts: any[] = [];
      dataDb.contacts
        .where('timestamp')
        .above(0)
        .and((contact) => {
          return contact && !contact.isNew && contact.isModified && !contact.isArchived;
        })
        .limit(500)
        .toArray()
        .then((entries) => {
          for (const entry of entries) {
            contacts.push({
              _id: entry._id,
              content: entry.content,
              filesToUpload: entry.filesToUpload,
              filesToDelete: entry.filesToDelete,
            });
          }
          resolve(contacts);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getNewProperties(): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const properties: any[] = [];
      dataDb.properties
        .where('timestamp')
        .above(0)
        .and((property) => {
          return property && property.isNew && !property.isArchived;
        })
        .limit(500)
        .toArray()
        .then((entries) => {
          for (const entry of entries) {
            properties.push({
              _id: entry._id,
              content: entry.content,
              filesToUpload: entry.filesToUpload,
              filesToDelete: entry.filesToDelete,
            });
          }
          resolve(properties);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getArchivedProperties(): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const properties: any[] = [];
      dataDb.properties
        .where('timestamp')
        .above(0)
        .and((property) => {
          return property && property.isArchived === 1;
        })
        .limit(500)
        .toArray()
        .then((entries) => {
          for (const entry of entries) {
            properties.push({
              _id: entry._id,
              isNew: entry.isNew,
              isModified: entry.isModified,
              content: entry.content,
              filesToUpload: entry.filesToUpload,
              filesToDelete: entry.filesToDelete,
            });
          }
          resolve(properties);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public getModifiedProperties(): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      const properties: any[] = [];
      dataDb.properties
        .where('timestamp')
        .above(0)
        .and((property) => {
          return property && !property.isNew && property.isModified && !property.isArchived;
        })
        .limit(500)
        .toArray()
        .then((entries) => {
          for (const entry of entries) {
            properties.push({
              _id: entry._id,
              content: entry.content,
              filesToUpload: entry.filesToUpload,
              filesToDelete: entry.filesToDelete,
            });
          }
          resolve(properties);
        })
        .catch((error) => {
          this.logger.error(error, false);
          reject(error);
        });
    });
  }

  public removeAccents(strAccents: string): string {
    if (!strAccents) {
      return '';
    }

    return strAccents.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
}

export class deepDiffMapper {
  private static readonly VALUE_CREATED = 'created';
  private static readonly VALUE_UPDATED = 'updated';
  private static readonly VALUE_DELETED = 'deleted';
  private static readonly VALUE_UNCHANGED = 'unchanged';

  protected isFunction(obj: object): boolean {
    return {}.toString.apply(obj) === '[object Function]';
  }

  protected isArray(obj: object): boolean {
    return {}.toString.apply(obj) === '[object Array]';
  }

  protected isObject(obj: object): boolean {
    return {}.toString.apply(obj) === '[object Object]';
  }

  protected isDate(obj: object): boolean {
    return {}.toString.apply(obj) === '[object Date]';
  }

  protected isValue(obj: object): boolean {
    return !this.isObject(obj) && !this.isArray(obj);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  protected compareValues(value1: any, value2: any): string {
    if (value1 === value2) {
      return deepDiffMapper.VALUE_UNCHANGED;
    }
    if (this.isDate(value1) && this.isDate(value2) && value1.getTime() === value2.getTime()) {
      return deepDiffMapper.VALUE_UNCHANGED;
    }
    if ('undefined' == typeof value1) {
      return deepDiffMapper.VALUE_CREATED;
    }
    if ('undefined' == typeof value2) {
      return deepDiffMapper.VALUE_DELETED;
    }

    return deepDiffMapper.VALUE_UPDATED;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public map(obj1: any, obj2: any): any {
    if (this.isFunction(obj1) || this.isFunction(obj2)) {
      throw 'Invalid argument. Function given, object expected.';
    }
    if (this.isValue(obj1) || this.isValue(obj2)) {
      return {
        type: this.compareValues(obj1, obj2),
        old: obj1,
        new: obj2,
      };
    }

    const diff: any = {};
    for (const key in obj1) {
      if (this.isFunction(obj1[key])) {
        continue;
      }

      let value2 = undefined;
      if ('undefined' != typeof obj2[key]) {
        value2 = obj2[key];
      }

      diff[key] = this.map(obj1[key], value2);
    }
    for (const key in obj2) {
      if (this.isFunction(obj2[key]) || 'undefined' != typeof diff[key]) {
        continue;
      }

      diff[key] = this.map(undefined, obj2[key]);
    }

    return diff;
  }
}
